// §9.17/D124 — Browser-Decode des source-video für den Spritesheet-Re-Key.
//
// Server-Referenz (Treue-Anker): `r2.decode_video` läuft `ffmpeg -i input -vf fps=24`
// (+ `-frames:v max`) — der fps-Filter rastert die Zeitachse auf t_k = k/fps und zeigt je
// Raster den Quell-Frame, dessen Präsentationszeit das Raster abdeckt (Duplikate/Drops je
// nach Quell-fps). Hier emuliert über <video> + seek(t_k) + drawImage: der Browser liefert
// beim Seek den zum Zeitstempel sichtbaren Frame — praktisch deckungsgleich; exakte
// Frame-Grenzfälle (ffmpeg-Rundung vs. Seek-Genauigkeit) und die YUV→RGB-Konversion
// (Browser-Decoder vs. ffmpeg-PNG, BT.601/709) können minimal abweichen. Für den Re-Key
// ist das Ergebnis SELBST-KONSISTENT (gekeyt wird exakt das, was der User sieht); die
// bewusste Abweichungs-Toleranz ist in D124 dokumentiert. <video>+seek statt
// WebCodecs+Demuxer: überall verfügbar (Safari!), kein MP4-Demuxing-Code — WebCodecs
// bleibt §13-Optimierung.

/** Server-Spiegel: r2.decode_video MAX_DECODE_FRAMES (D86-OOM-Schutz). */
export const MAX_DECODE_FRAMES = 600;

export interface DecodeOptions {
  /** Ziel-fps-Raster (Server-Kette: 24). */
  fps: number;
  /** Hartes Frame-Cap (z.B. die Re-Key-Präfix-Länge — mehr wird gar nicht erst dekodiert). */
  maxFrames: number;
  /** Optionaler Downscale (0<scale≤1) für die Live-Vorschau (volle Auflösung beim Speichern). */
  scale?: number;
  /** Abbruch (Codex-P1-D, D124-R1): Schließen/Wegnavigieren stoppt den Decode SOFORT —
   *  sonst liefe der teure Save-Flow weiter und würde am Ende noch Credits materialisieren. */
  signal?: AbortSignal;
}

function waitEvent(el: HTMLMediaElement, ok: string, fail = "error", signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    // Codex-P2-Verify (D131): ein Abort WÄHREND eines Event-Waits (loadedmetadata/seeked)
    // rejected sofort — sonst hinge der Decode bis zum nächsten Schleifen-Checkpoint
    // (oder ewig, wenn das Video-Element nach Unmount kein Event mehr feuert).
    if (signal?.aborted) {
      reject(new DOMException("Decode abgebrochen", "AbortError"));
      return;
    }
    const onOk = () => {
      cleanup();
      resolve();
    };
    const onFail = () => {
      cleanup();
      reject(new Error("Video-Decode fehlgeschlagen"));
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException("Decode abgebrochen", "AbortError"));
    };
    const cleanup = () => {
      el.removeEventListener(ok, onOk);
      el.removeEventListener(fail, onFail);
      signal?.removeEventListener("abort", onAbort);
    };
    el.addEventListener(ok, onOk, { once: true });
    el.addEventListener(fail, onFail, { once: true });
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Video-Blob → RGBA-ImageData-Frames auf dem fps-Raster (sequentieller AsyncGenerator —
 * der Aufrufer transferiert jeden Frame sofort in den Worker, es liegt nie die ganze
 * Sequenz im JS-Heap). Der Blob MUSS same-origin lesbar sein (Bytes-Proxy-Route /
 * Object-URL) — sonst wäre das Canvas tainted.
 */
export async function* decodeVideoFrames(blob: Blob, opts: DecodeOptions): AsyncGenerator<ImageData> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = url;
  try {
    await waitEvent(video, "loadedmetadata", "error", opts.signal);
    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0) throw new Error("Video ohne lesbare Dauer");
    const scale = Math.min(Math.max(opts.scale ?? 1, 0.05), 1);
    const w = Math.max(1, Math.round(video.videoWidth * scale));
    const h = Math.max(1, Math.round(video.videoHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Canvas-2D nicht verfügbar");

    const fps = Math.max(1, Math.trunc(opts.fps));
    const cap = Math.min(Math.max(1, Math.trunc(opts.maxFrames)), MAX_DECODE_FRAMES);
    for (let k = 0; k < cap; k++) {
      if (opts.signal?.aborted) throw new DOMException("Decode abgebrochen", "AbortError");
      const t = k / fps;
      if (t >= duration) break; // ffmpeg liefert keine Frames hinter dem Clip-Ende
      // Seek minimal VOR dem exakten Rasterpunkt vermeidet, dass Browser am Frame-Übergang
      // schon den NÄCHSTEN Frame zeigen (ffmpeg-fps zeigt den Frame, der t_k abdeckt).
      video.currentTime = Math.min(t + 1e-4, Math.max(0, duration - 1e-4));
      await waitEvent(video, "seeked", "error", opts.signal);
      ctx.drawImage(video, 0, 0, w, h);
      yield ctx.getImageData(0, 0, w, h);
    }
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
}

/** Metadaten ohne Frame-Decode (D130): Dimensionen + Dauer für Pixel-Budget-Skalierung. */
export async function probeVideoMeta(
  blob: Blob,
  signal?: AbortSignal,
): Promise<{ width: number; height: number; duration: number }> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "metadata";
  video.src = url;
  try {
    await waitEvent(video, "loadedmetadata", "error", signal);
    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0) throw new Error("Video ohne lesbare Dauer");
    return { width: video.videoWidth, height: video.videoHeight, duration };
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
}

/** Genau einen Frame am fps-Raster holen (Live-Vorschau eines repräsentativen Frames). */
export async function decodeSingleFrame(
  blob: Blob,
  frameIndex: number,
  opts: DecodeOptions,
): Promise<ImageData | null> {
  let i = 0;
  for await (const frame of decodeVideoFrames(blob, { ...opts, maxFrames: frameIndex + 1 })) {
    if (i === frameIndex) return frame;
    i += 1;
  }
  return null;
}
